import { CognitoUserPool, CognitoUser, AuthenticationDetails } from 'amazon-cognito-identity-js';

const POOL_ID = process.env.REACT_APP_COGNITO_USER_POOL_ID;
const CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID;

let userPool = null;

function getUserPool() {
  if (!userPool && POOL_ID && CLIENT_ID) {
    userPool = new CognitoUserPool({
      UserPoolId: POOL_ID,
      ClientId: CLIENT_ID,
    });
  }
  return userPool;
}

/**
 * commons #282: Cognito usernames are case-SENSITIVE on our pool (no
 * UsernameConfiguration, no email alias — both fixed at pool creation), so the
 * string we send has to match the stored username exactly. Two things made
 * that fail: accounts created with a capital (Shawna.taylor@…) and — for every
 * account — a phone keyboard autocapitalising the first letter of a typed
 * address. The server now creates usernames lowercased, so lowercasing here
 * makes the two ends agree no matter how the address is typed.
 */
function normaliseUsername(email) {
  return String(email || '').trim().toLowerCase();
}

class CognitoAuthServiceClient {
  /**
   * Check if Cognito auth is configured
   */
  isConfigured() {
    return process.env.REACT_APP_AUTH_PROVIDER === 'cognito' && !!POOL_ID && !!CLIENT_ID;
  }

  /**
   * Sign in with email and password via Cognito.
   * @param {string} email
   * @param {string} password
   * @returns {Promise<{idToken: string, accessToken: string, refreshToken: string}>}
   */
  signIn(email, password) {
    return new Promise((resolve, reject) => {
      const pool = getUserPool();
      if (!pool) {
        return reject(new Error('Cognito is not configured'));
      }

      const username = normaliseUsername(email);

      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: pool,
      });

      const authDetails = new AuthenticationDetails({
        Username: username,
        Password: password,
      });

      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (session) => {
          resolve({
            idToken: session.getIdToken().getJwtToken(),
            accessToken: session.getAccessToken().getJwtToken(),
            refreshToken: session.getRefreshToken().getToken(),
          });
        },
        onFailure: (err) => {
          // GH-11: PasswordResetRequiredException lands in onFailure when the
          // SDK isn't given a dedicated onPasswordResetRequired callback (we
          // provide one below). Surface it with the same shape so the Login
          // UI can switch to the forgot-password flow instead of showing the
          // catch-all "Invalid email or password" toast.
          if (err && (err.code === 'PasswordResetRequiredException' || err.name === 'PasswordResetRequiredException')) {
            return reject({
              code: 'PASSWORD_RESET_REQUIRED',
              cognitoUser,
              message: 'Password reset required. Check your email for a verification code, then set a new password.',
            });
          }
          reject(err);
        },
        newPasswordRequired: (userAttributes) => {
          // User must set a new password (first login with temp password)
          // Store the cognitoUser for the password change flow
          reject({
            code: 'NEW_PASSWORD_REQUIRED',
            cognitoUser,
            userAttributes,
            message: 'New password required. Please set a new password.',
          });
        },
        // GH-11: explicit callback for the PASSWORD_RESET_REQUIRED challenge.
        // This fires when a super-admin reset the user's password and they
        // haven't completed the forgot-password flow yet. Without this, the
        // SDK falls into onFailure with PasswordResetRequiredException and
        // the UI shows "Invalid email or password" — that's been blocking
        // sai@/koushik@ since 2026-04-21.
        onPasswordResetRequired: () => {
          reject({
            code: 'PASSWORD_RESET_REQUIRED',
            cognitoUser,
            message: 'Password reset required. Check your email for a verification code, then set a new password.',
          });
        },
      });
    });
  }

  /**
   * Initiate the forgot-password flow. Cognito emails a verification code
   * to the user (via SES). Pair with `confirmForgotPassword` to set a new
   * permanent password.
   * @param {string} email
   * @returns {Promise<{CodeDeliveryDetails: object}>}
   */
  forgotPassword(email) {
    return new Promise((resolve, reject) => {
      const pool = getUserPool();
      if (!pool) return reject(new Error('Cognito is not configured'));

      const cognitoUser = new CognitoUser({ Username: normaliseUsername(email), Pool: pool });
      cognitoUser.forgotPassword({
        onSuccess: (data) => resolve(data),
        onFailure: (err) => reject(err),
        inputVerificationCode: (data) => resolve(data),
      });
    });
  }

  /**
   * Submit the verification code + new password to complete a forgot-password
   * flow. Cognito moves the user to CONFIRMED + sets the new password as
   * permanent.
   * @param {string} email
   * @param {string} verificationCode - From the email Cognito sent
   * @param {string} newPassword
   * @returns {Promise<void>}
   */
  confirmForgotPassword(email, verificationCode, newPassword) {
    return new Promise((resolve, reject) => {
      const pool = getUserPool();
      if (!pool) return reject(new Error('Cognito is not configured'));

      const cognitoUser = new CognitoUser({ Username: normaliseUsername(email), Pool: pool });
      cognitoUser.confirmPassword(verificationCode, newPassword, {
        onSuccess: () => resolve(),
        onFailure: (err) => reject(err),
      });
    });
  }

  /**
   * Complete new password challenge (first login with temporary password).
   * @param {CognitoUser} cognitoUser - From newPasswordRequired callback
   * @param {string} newPassword
   * @returns {Promise<{idToken: string, accessToken: string, refreshToken: string}>}
   */
  completeNewPassword(cognitoUser, newPassword) {
    return new Promise((resolve, reject) => {
      cognitoUser.completeNewPasswordChallenge(newPassword, {}, {
        onSuccess: (session) => {
          resolve({
            idToken: session.getIdToken().getJwtToken(),
            accessToken: session.getAccessToken().getJwtToken(),
            refreshToken: session.getRefreshToken().getToken(),
          });
        },
        onFailure: (err) => {
          reject(err);
        },
      });
    });
  }

  /**
   * Sign out the current user.
   */
  signOut() {
    const pool = getUserPool();
    if (!pool) return;

    const currentUser = pool.getCurrentUser();
    if (currentUser) {
      currentUser.signOut();
    }
  }

  /**
   * Change password for the current authenticated user.
   * @param {string} oldPassword
   * @param {string} newPassword
   * @returns {Promise<void>}
   */
  changePassword(oldPassword, newPassword) {
    return new Promise((resolve, reject) => {
      const pool = getUserPool();
      if (!pool) return reject(new Error('Cognito is not configured'));

      const currentUser = pool.getCurrentUser();
      if (!currentUser) return reject(new Error('No authenticated user'));

      currentUser.getSession((err) => {
        if (err) return reject(err);

        currentUser.changePassword(oldPassword, newPassword, (err2, result) => {
          if (err2) return reject(err2);
          resolve(result);
        });
      });
    });
  }

  /**
   * Get current session (auto-refreshes if expired).
   * @returns {Promise<{idToken: string, accessToken: string}|null>}
   */
  getSession() {
    return new Promise((resolve) => {
      const pool = getUserPool();
      if (!pool) return resolve(null);

      const currentUser = pool.getCurrentUser();
      if (!currentUser) return resolve(null);

      currentUser.getSession((err, session) => {
        if (err || !session || !session.isValid()) {
          return resolve(null);
        }
        resolve({
          idToken: session.getIdToken().getJwtToken(),
          accessToken: session.getAccessToken().getJwtToken(),
        });
      });
    });
  }
}

const cognitoAuthService = new CognitoAuthServiceClient();
export default cognitoAuthService;
